/**
 * FluencyDirectController.js
 * 
 * Dynamische Sprachsteuerung für Fluency Direct
 * Scannt das DOM nach steuerbaren Elementen und generiert JSGF-Grammatik.
 * 
 * Globale Funktionen (von Fluency Direct aufgerufen):
 * - window.fdGetGrammar() - Liefert aktuelle JSGF-Grammatik
 * - window.fdExecuteCommand(id) - Führt Befehl aus (klickt Element)
 * 
 * Debug-Funktionen:
 * - window.showVoiceTargets() - Zeigt alle steuerbaren Elemente
 * - window.testVoiceCommand(text) - Testet einen Sprachbefehl
 */

// ============================================================================
// KONFIGURATION
// ============================================================================

/**
 * Deutsche Aktionsverben mit Varianten
 * Format: [Hauptverb, ...Varianten]
 */
const ACTION_VERBS_DE = [
  ['öffne', 'öffnen', 'open'],
  ['zeige', 'zeigen', 'anzeigen', 'show'],
  ['wähle', 'wählen', 'auswählen', 'select'],
  ['klicke', 'klick', 'klicken', 'click'],
  ['gehe zu', 'geh zu', 'navigiere zu', 'goto'],
  ['schließe', 'schließen', 'close'],
  ['speichere', 'speichern', 'save'],
  ['lösche', 'löschen', 'delete'],
  ['bearbeite', 'bearbeiten', 'edit'],
  ['starte', 'starten', 'start'],
  ['stoppe', 'stoppen', 'stop']
];

/**
 * Englische Aktionsverben mit Varianten
 */
const ACTION_VERBS_EN = [
  ['open'],
  ['show', 'display'],
  ['select', 'choose'],
  ['click'],
  ['go to', 'navigate to'],
  ['close'],
  ['save'],
  ['delete', 'remove'],
  ['edit'],
  ['start'],
  ['stop']
];

/**
 * Französische Aktionsverben mit Varianten
 */
const ACTION_VERBS_FR = [
  ['ouvre', 'ouvrir'],
  ['montre', 'montrer', 'affiche', 'afficher'],
  ['sélectionne', 'sélectionner', 'choisis', 'choisir'],
  ['clique', 'cliquer'],
  ['va à', 'aller à', 'naviguer vers'],
  ['ferme', 'fermer'],
  ['enregistre', 'enregistrer', 'sauvegarde', 'sauvegarder'],
  ['supprime', 'supprimer'],
  ['modifie', 'modifier', 'édite', 'éditer'],
  ['démarre', 'démarrer'],
  ['arrête', 'arrêter']
];

/**
 * Selektoren für steuerbare Elemente
 */
const CLICKABLE_SELECTORS = [
  // Buttons
  'button',
  '[role="button"]',
  // Links
  'a[href]',
  // Klickbare Elemente mit ID
  '[id][onclick]',
  // MUI Komponenten
  '.MuiButton-root',
  '.MuiIconButton-root',
  '.MuiMenuItem-root',
  '.MuiListItem-root',
  '.MuiTab-root',
  '.MuiChip-root',
  '.MuiTableRow-root',
  '.MuiCard-root[id]',  // Dashboard Cards mit ID
  // Dropdown-Items
  '[role="option"]',
  '[role="menuitem"]',
  '[role="listitem"]',
  '[role="tab"]',
  // Tabellenzeilen - MUI DataGrid
  '.MuiDataGrid-row',
  'tr[data-row-id]',
  '[role="row"][data-id]',
  // Custom steuerbare Elemente
  '[data-voice-target]',
  '[data-voice-label]',  // Elemente mit explizitem Voice Label
  // Elemente mit expliziter ID die steuerbar sein sollen
  '[id$="Button"]',
  '[id$="Tab"]',
  '[id$="Row"]',
  '[id$="Item"]',
  '[id$="Link"]',
  '[id$="Option"]',
  '[id^="dashboardCard_"]'  // Dashboard Cards
];

/**
 * Spezielle Befehle die keine Element-ID benötigen
 * Format: { phrase: [Varianten], action: 'actionName' }
 */
const SPECIAL_COMMANDS_DE = [
  // Navigation mit und ohne Aktionsverb - Patient UND Untersuchung als Synonyme
  { phrases: ['nächster Patient', 'nächste Untersuchung', 'nächster', 'weiter', 'next', 'zeige nächsten Patienten', 'zeige nächste Untersuchung', 'öffne nächsten Patienten', 'öffne nächste Untersuchung', 'nächsten Patienten'], action: 'nextPatient' },
  { phrases: ['vorheriger Patient', 'vorherige Untersuchung', 'vorheriger', 'zurück', 'previous', 'zeige vorherigen Patienten', 'zeige vorherige Untersuchung', 'öffne vorherigen Patienten', 'öffne vorherige Untersuchung', 'vorherigen Patienten'], action: 'previousPatient' },
  { phrases: ['erster Patient', 'erste Untersuchung', 'erster', 'first', 'zeige ersten Patienten', 'zeige erste Untersuchung', 'öffne ersten Patienten', 'öffne erste Untersuchung', 'ersten Patienten'], action: 'firstPatient' },
  { phrases: ['letzter Patient', 'letzte Untersuchung', 'letzter', 'last', 'zeige letzten Patienten', 'zeige letzte Untersuchung', 'öffne letzten Patienten', 'öffne letzte Untersuchung', 'letzten Patienten'], action: 'lastPatient' },
  // Nummerierte Patienten/Untersuchungen (1-10)
  { phrases: ['erster Patient', 'erste Untersuchung', 'ersten Patienten', 'Patient eins', 'Patient 1', 'zeige ersten Patienten', 'zeige erste Untersuchung', 'öffne ersten Patienten', 'öffne erste Untersuchung', '1. Patient', '1. Patienten', '1. Untersuchung'], action: 'patient_1' },
  { phrases: ['zweiter Patient', 'zweite Untersuchung', 'zweiten Patienten', 'Patient zwei', 'Patient 2', 'zeige zweiten Patienten', 'zeige zweite Untersuchung', 'öffne zweiten Patienten', 'öffne zweite Untersuchung', '2. Patient', '2. Patienten', '2. Untersuchung'], action: 'patient_2' },
  { phrases: ['dritter Patient', 'dritte Untersuchung', 'dritten Patienten', 'Patient drei', 'Patient 3', 'zeige dritten Patienten', 'zeige dritte Untersuchung', 'öffne dritten Patienten', 'öffne dritte Untersuchung', '3. Patient', '3. Patienten', '3. Untersuchung'], action: 'patient_3' },
  { phrases: ['vierter Patient', 'vierte Untersuchung', 'vierten Patienten', 'Patient vier', 'Patient 4', 'zeige vierten Patienten', 'zeige vierte Untersuchung', 'öffne vierten Patienten', 'öffne vierte Untersuchung', '4. Patient', '4. Patienten', '4. Untersuchung'], action: 'patient_4' },
  { phrases: ['fünfter Patient', 'fünfte Untersuchung', 'fünften Patienten', 'Patient fünf', 'Patient 5', 'zeige fünften Patienten', 'zeige fünfte Untersuchung', 'öffne fünften Patienten', 'öffne fünfte Untersuchung', '5. Patient', '5. Patienten', '5. Untersuchung'], action: 'patient_5' },
  { phrases: ['sechster Patient', 'sechste Untersuchung', 'sechsten Patienten', 'Patient sechs', 'Patient 6', 'zeige sechsten Patienten', 'zeige sechste Untersuchung', 'öffne sechsten Patienten', 'öffne sechste Untersuchung', '6. Patient', '6. Patienten', '6. Untersuchung'], action: 'patient_6' },
  { phrases: ['siebter Patient', 'siebte Untersuchung', 'siebten Patienten', 'Patient sieben', 'Patient 7', 'zeige siebten Patienten', 'zeige siebte Untersuchung', 'öffne siebten Patienten', 'öffne siebte Untersuchung', '7. Patient', '7. Patienten', '7. Untersuchung'], action: 'patient_7' },
  { phrases: ['achter Patient', 'achte Untersuchung', 'achten Patienten', 'Patient acht', 'Patient 8', 'zeige achten Patienten', 'zeige achte Untersuchung', 'öffne achten Patienten', 'öffne achte Untersuchung', '8. Patient', '8. Patienten', '8. Untersuchung'], action: 'patient_8' },
  { phrases: ['neunter Patient', 'neunte Untersuchung', 'neunten Patienten', 'Patient neun', 'Patient 9', 'zeige neunten Patienten', 'zeige neunte Untersuchung', 'öffne neunten Patienten', 'öffne neunte Untersuchung', '9. Patient', '9. Patienten', '9. Untersuchung'], action: 'patient_9' },
  { phrases: ['zehnter Patient', 'zehnte Untersuchung', 'zehnten Patienten', 'Patient zehn', 'Patient 10', 'zeige zehnten Patienten', 'zeige zehnte Untersuchung', 'öffne zehnten Patienten', 'öffne zehnte Untersuchung', '10. Patient', '10. Patienten', '10. Untersuchung'], action: 'patient_10' },
  // Status-Filter (mit Singular/Plural-Varianten für bessere Erkennung)
  { phrases: ['alle Untersuchungen', 'alle Untersuchung', 'Filter alle', 'alle anzeigen', 'zeige alles', 'alles zeigen', 'zeige alle Untersuchungen', 'zeige alle Untersuchung', 'zeig alle Untersuchungen', 'zeig alle Untersuchung', 'alle zeigen'], action: 'filter_all' },
  { phrases: ['neue Untersuchungen', 'neue Untersuchung', 'nur neue', 'Filter neu', 'nur Neue zeigen', 'Neue zeigen', 'zeige nur neue', 'zeig nur neue', 'zeige neue Untersuchungen', 'zeige neue Untersuchung', 'zeig neue Untersuchungen', 'zeig neue Untersuchung', 'zeige nur neue Untersuchungen', 'zeige nur neue Untersuchung'], action: 'filter_new' },
  { phrases: ['genehmigte Untersuchungen', 'genehmigte Untersuchung', 'nur genehmigte', 'Filter genehmigt', 'nur Genehmigte zeigen', 'Genehmigte zeigen', 'zeige nur genehmigte', 'zeig nur genehmigte', 'zeige genehmigte Untersuchungen', 'zeige genehmigte Untersuchung', 'zeig genehmigte Untersuchungen', 'zeig genehmigte Untersuchung', 'zeige nur genehmigte Untersuchungen', 'zeige nur genehmigte Untersuchung'], action: 'filter_approved' },
  { phrases: ['abgelehnte Untersuchungen', 'abgelehnte Untersuchung', 'nur abgelehnte', 'Filter abgelehnt', 'nur Abgelehnte zeigen', 'Abgelehnte zeigen', 'zeige nur abgelehnte', 'zeig nur abgelehnte', 'zeige abgelehnte Untersuchungen', 'zeige abgelehnte Untersuchung', 'zeig abgelehnte Untersuchungen', 'zeig abgelehnte Untersuchung', 'zeige nur abgelehnte Untersuchungen', 'zeige nur abgelehnte Untersuchung'], action: 'filter_rejected' },
  // Vorbefund-Suche
  { phrases: ['letzter Vorbefund', 'zeige letzten Vorbefund', 'zeig letzten Vorbefund', 'letzten Vorbefund zeigen', 'passender Vorbefund', 'zeige passenden Vorbefund', 'ähnlicher Vorbefund', 'Vorbefund suchen', 'letzte Voruntersuchung', 'zeige letzte Voruntersuchung', 'zeig letzte Voruntersuchung', 'letzte Voruntersuchung zeigen', 'letzten Vorbefund anzeigen', 'letzte Voruntersuchung anzeigen'], action: 'prior_study_last' },
  { phrases: ['erster Vorbefund', 'zeige ersten Vorbefund', 'zeig ersten Vorbefund', 'ältester Vorbefund', 'erste Voruntersuchung', 'zeige erste Voruntersuchung', 'zeig erste Voruntersuchung', 'älteste Voruntersuchung'], action: 'prior_study_first' },
  { phrases: ['nächster Vorbefund', 'zeige nächsten Vorbefund', 'Vorbefund weiter', 'nächste Voruntersuchung', 'zeige nächste Voruntersuchung'], action: 'prior_study_next' },
  { phrases: ['vorheriger Vorbefund', 'zeige vorherigen Vorbefund', 'Vorbefund zurück', 'vorherige Voruntersuchung', 'zeige vorherige Voruntersuchung', 'zeig vorherige Voruntersuchung'], action: 'prior_study_previous' },
  // Datums-Filter
  { phrases: ['heute', 'nur heute', 'zeige heute', 'zeig heute', 'heutige Untersuchungen', 'heutige Untersuchung', 'von heute'], action: 'date_today' },
  { phrases: ['gestern', 'nur gestern', 'zeige gestern', 'zeig gestern', 'gestrige Untersuchungen', 'gestrige Untersuchung', 'von gestern'], action: 'date_yesterday' },
  { phrases: ['morgen', 'nur morgen', 'zeige morgen', 'zeig morgen', 'morgige Untersuchungen', 'morgige Untersuchung', 'von morgen'], action: 'date_tomorrow' },
  { phrases: ['Datum löschen', 'Datum zurücksetzen', 'Datumsfilter löschen', 'Datumsfilter zurücksetzen', 'kein Datum', 'alle Tage', 'alle Daten'], action: 'date_clear' },
  // Modalitäten-Filter (mit Singular/Plural-Varianten)
  { phrases: ['alle Modalitäten', 'alle Geräte', 'Modalität alle', 'zeige alle Modalitäten'], action: 'modality_all' },
  { phrases: ['CT Untersuchungen', 'CT Untersuchung', 'nur CT', 'zeige CT', 'Computertomographie', 'CT zeigen'], action: 'modality_CT' },
  { phrases: ['MRT Untersuchungen', 'MRT Untersuchung', 'nur MRT', 'zeige MRT', 'MR Untersuchungen', 'MR Untersuchung', 'nur MR', 'zeige MR', 'Magnetresonanz', 'Kernspintomographie', 'Kernspin'], action: 'modality_MR' },
  { phrases: ['Röntgen Untersuchungen', 'Röntgen Untersuchung', 'nur Röntgen', 'zeige Röntgen', 'Röntgenuntersuchungen', 'Röntgenuntersuchung', 'RÖ zeigen'], action: 'modality_DX' },
  { phrases: ['Ultraschall Untersuchungen', 'Ultraschall Untersuchung', 'nur Ultraschall', 'zeige Ultraschall', 'Sono', 'Sonographie', 'US zeigen'], action: 'modality_US' },
  { phrases: ['Angio Untersuchungen', 'Angio Untersuchung', 'nur Angio', 'zeige Angio', 'Angiographie', 'XA zeigen', 'Angiografien'], action: 'modality_XA' },
  { phrases: ['Mammographie Untersuchungen', 'Mammographie Untersuchung', 'nur Mammographie', 'zeige Mammographie', 'Mammo', 'MG zeigen'], action: 'modality_MG' },
  { phrases: ['Nuklearmedizin Untersuchungen', 'Nuklearmedizin Untersuchung', 'nur Nuklearmedizin', 'zeige Nuklearmedizin', 'NM zeigen', 'Szintigraphie'], action: 'modality_NM' },
  { phrases: ['PET Untersuchungen', 'PET Untersuchung', 'nur PET', 'zeige PET', 'Positronen', 'PT zeigen'], action: 'modality_PT' },
  { phrases: ['Fluoroskopie Untersuchungen', 'Fluoroskopie Untersuchung', 'nur Fluoroskopie', 'zeige Fluoroskopie', 'Durchleuchtung', 'RF zeigen'], action: 'modality_RF' },
  { phrases: ['Endoskopie Untersuchungen', 'Endoskopie Untersuchung', 'nur Endoskopie', 'zeige Endoskopie', 'ES zeigen'], action: 'modality_ES' },
  // Feld-Fokus Befehle
  { phrases: ['Kommentar Doppelpunkt', 'Kommentar:', 'Kommentar', 'in Kommentar', 'zum Kommentar', 'Kommentarfeld', 'Kommentar eingeben'], action: 'focus_comment' },
  { phrases: ['Protokoll Doppelpunkt', 'Protokoll:', 'Protokoll', 'in Protokoll', 'zum Protokoll', 'Protokollfeld', 'Untersuchungsprotokoll'], action: 'focus_protocol' },
  // Dialog-Befehle
  { phrases: ['Protokolle öffnen', 'öffne Protokolle', 'Protokoll Dialog', 'Scan protokolle','Protokolle','Scan protokolle', 'Protokollauswahl', 'Protokolldialog öffnen'], action: 'open_protocol_dialog' },
  { phrases: ['Dialog schließen', 'schließen', 'abbrechen', 'Dialog beenden', 'schließe Dialog'], action: 'close_dialog' }
];

const SPECIAL_COMMANDS_EN = [
  { phrases: ['next patient', 'next', 'show next patient', 'open next patient'], action: 'nextPatient' },
  { phrases: ['previous patient', 'previous', 'back', 'show previous patient', 'open previous patient'], action: 'previousPatient' },
  { phrases: ['first patient', 'first', 'show first patient', 'open first patient'], action: 'firstPatient' },
  { phrases: ['last patient', 'last', 'show last patient', 'open last patient'], action: 'lastPatient' },
  // Numbered patients
  { phrases: ['first patient', 'patient one', 'patient 1', '1st patient'], action: 'patient_1' },
  { phrases: ['second patient', 'patient two', 'patient 2', '2nd patient'], action: 'patient_2' },
  { phrases: ['third patient', 'patient three', 'patient 3', '3rd patient'], action: 'patient_3' },
  { phrases: ['fourth patient', 'patient four', 'patient 4', '4th patient'], action: 'patient_4' },
  { phrases: ['fifth patient', 'patient five', 'patient 5', '5th patient'], action: 'patient_5' },
  { phrases: ['sixth patient', 'patient six', 'patient 6', '6th patient'], action: 'patient_6' },
  { phrases: ['seventh patient', 'patient seven', 'patient 7', '7th patient'], action: 'patient_7' },
  { phrases: ['eighth patient', 'patient eight', 'patient 8', '8th patient'], action: 'patient_8' },
  { phrases: ['ninth patient', 'patient nine', 'patient 9', '9th patient'], action: 'patient_9' },
  { phrases: ['tenth patient', 'patient ten', 'patient 10', '10th patient'], action: 'patient_10' },
  // Status filter
  { phrases: ['all exams', 'filter all', 'show everything', 'show all exams'], action: 'filter_all' },
  { phrases: ['new exams', 'only new', 'filter new', 'show only new', 'show new exams'], action: 'filter_new' },
  { phrases: ['approved exams', 'only approved', 'filter approved', 'show only approved', 'show approved exams'], action: 'filter_approved' },
  { phrases: ['rejected exams', 'only rejected', 'filter rejected', 'show only rejected', 'show rejected exams'], action: 'filter_rejected' },
  // Prior study search
  { phrases: ['last prior study', 'show last prior study', 'matching prior study', 'show matching prior', 'find prior study'], action: 'prior_study_last' },
  { phrases: ['first prior study', 'show first prior study', 'oldest prior study'], action: 'prior_study_first' },
  { phrases: ['next prior study', 'show next prior study', 'prior next'], action: 'prior_study_next' },
  { phrases: ['previous prior study', 'show previous prior study', 'prior back'], action: 'prior_study_previous' },
  // Date filter
  { phrases: ['today', 'only today', 'show today', 'today exams', 'from today'], action: 'date_today' },
  { phrases: ['yesterday', 'only yesterday', 'show yesterday', 'yesterday exams', 'from yesterday'], action: 'date_yesterday' },
  { phrases: ['tomorrow', 'only tomorrow', 'show tomorrow', 'tomorrow exams', 'from tomorrow'], action: 'date_tomorrow' },
  { phrases: ['clear date', 'reset date', 'clear date filter', 'no date', 'all dates'], action: 'date_clear' },
  // Modality filter
  { phrases: ['all modalities', 'modality all', 'show all modalities'], action: 'modality_all' },
  { phrases: ['CT exams', 'only CT', 'show CT', 'computed tomography'], action: 'modality_CT' },
  { phrases: ['MRI exams', 'only MRI', 'show MRI', 'MR exams', 'magnetic resonance'], action: 'modality_MR' },
  { phrases: ['X-ray exams', 'only X-ray', 'show X-ray', 'radiography'], action: 'modality_DX' },
  { phrases: ['ultrasound exams', 'only ultrasound', 'show ultrasound', 'sonography'], action: 'modality_US' },
  { phrases: ['angiography exams', 'only angio', 'show angio'], action: 'modality_XA' },
  { phrases: ['mammography exams', 'only mammography', 'show mammography'], action: 'modality_MG' },
  { phrases: ['nuclear medicine exams', 'only nuclear', 'show nuclear medicine'], action: 'modality_NM' },
  { phrases: ['PET exams', 'only PET', 'show PET'], action: 'modality_PT' },
  { phrases: ['fluoroscopy exams', 'only fluoroscopy', 'show fluoroscopy'], action: 'modality_RF' },
  { phrases: ['endoscopy exams', 'only endoscopy', 'show endoscopy'], action: 'modality_ES' },
  // Field focus commands
  { phrases: ['comment colon', 'comment:', 'comment', 'to comment', 'comment field', 'enter comment'], action: 'focus_comment' },
  { phrases: ['protocol colon', 'protocol:', 'protocol','protocols', 'to protocol', 'protocol field', 'scan protocol'], action: 'focus_protocol' },
  // Dialog commands
  { phrases: ['open protocols', 'show protocols', 'protocol dialog', 'scan protocols', 'protocol selection', 'open protocol dialog'], action: 'open_protocol_dialog' },
  { phrases: ['close dialog', 'close', 'cancel', 'dismiss', 'close window'], action: 'close_dialog' }
];

const SPECIAL_COMMANDS_FR = [
  { phrases: ['patient suivant', 'suivant', 'prochain', 'montrer patient suivant', 'ouvrir patient suivant'], action: 'nextPatient' },
  { phrases: ['patient précédent', 'précédent', 'retour', 'montrer patient précédent', 'ouvrir patient précédent'], action: 'previousPatient' },
  { phrases: ['premier patient', 'premier', 'montrer premier patient', 'ouvrir premier patient'], action: 'firstPatient' },
  { phrases: ['dernier patient', 'dernier', 'montrer dernier patient', 'ouvrir dernier patient'], action: 'lastPatient' },
  // Patients numérotés
  { phrases: ['premier patient', 'patient un', 'patient 1', '1er patient'], action: 'patient_1' },
  { phrases: ['deuxième patient', 'patient deux', 'patient 2', '2ème patient'], action: 'patient_2' },
  { phrases: ['troisième patient', 'patient trois', 'patient 3', '3ème patient'], action: 'patient_3' },
  { phrases: ['quatrième patient', 'patient quatre', 'patient 4', '4ème patient'], action: 'patient_4' },
  { phrases: ['cinquième patient', 'patient cinq', 'patient 5', '5ème patient'], action: 'patient_5' },
  { phrases: ['sixième patient', 'patient six', 'patient 6', '6ème patient'], action: 'patient_6' },
  { phrases: ['septième patient', 'patient sept', 'patient 7', '7ème patient'], action: 'patient_7' },
  { phrases: ['huitième patient', 'patient huit', 'patient 8', '8ème patient'], action: 'patient_8' },
  { phrases: ['neuvième patient', 'patient neuf', 'patient 9', '9ème patient'], action: 'patient_9' },
  { phrases: ['dixième patient', 'patient dix', 'patient 10', '10ème patient'], action: 'patient_10' },
  // Filtre de statut
  { phrases: ['tous les examens', 'filtre tous', 'tout montrer', 'montrer tous les examens'], action: 'filter_all' },
  { phrases: ['nouveaux examens', 'seulement nouveaux', 'filtre nouveau', 'montrer seulement nouveaux', 'montrer nouveaux examens'], action: 'filter_new' },
  { phrases: ['examens approuvés', 'seulement approuvés', 'filtre approuvé', 'montrer seulement approuvés', 'montrer examens approuvés'], action: 'filter_approved' },
  { phrases: ['examens rejetés', 'seulement rejetés', 'filtre rejeté', 'montrer seulement rejetés', 'montrer examens rejetés'], action: 'filter_rejected' },
  // Recherche d'antécédent
  { phrases: ['dernier antécédent', 'montrer dernier antécédent', 'antécédent correspondant', 'chercher antécédent'], action: 'prior_study_last' },
  { phrases: ['premier antécédent', 'montrer premier antécédent', 'plus ancien antécédent'], action: 'prior_study_first' },
  { phrases: ['antécédent suivant', 'montrer antécédent suivant'], action: 'prior_study_next' },
  { phrases: ['antécédent précédent', 'montrer antécédent précédent'], action: 'prior_study_previous' },
  // Filtre de date
  { phrases: ['aujourd hui', 'seulement aujourd hui', 'montrer aujourd hui', 'examens d aujourd hui'], action: 'date_today' },
  { phrases: ['hier', 'seulement hier', 'montrer hier', 'examens d hier'], action: 'date_yesterday' },
  { phrases: ['demain', 'seulement demain', 'montrer demain', 'examens de demain'], action: 'date_tomorrow' },
  { phrases: ['effacer date', 'réinitialiser date', 'pas de date', 'toutes les dates'], action: 'date_clear' },
  // Filtre de modalité
  { phrases: ['toutes modalités', 'modalité tous', 'montrer toutes modalités'], action: 'modality_all' },
  { phrases: ['examens CT', 'seulement CT', 'montrer CT', 'scanner'], action: 'modality_CT' },
  { phrases: ['examens IRM', 'seulement IRM', 'montrer IRM', 'résonance magnétique'], action: 'modality_MR' },
  { phrases: ['examens radiographie', 'seulement radiographie', 'montrer radiographie', 'rayons X'], action: 'modality_DX' },
  { phrases: ['examens échographie', 'seulement échographie', 'montrer échographie', 'ultrasons'], action: 'modality_US' },
  { phrases: ['examens angiographie', 'seulement angio', 'montrer angio'], action: 'modality_XA' },
  { phrases: ['examens mammographie', 'seulement mammographie', 'montrer mammographie'], action: 'modality_MG' },
  { phrases: ['examens médecine nucléaire', 'seulement nucléaire', 'montrer médecine nucléaire'], action: 'modality_NM' },
  { phrases: ['examens TEP', 'seulement TEP', 'montrer TEP'], action: 'modality_PT' },
  { phrases: ['examens fluoroscopie', 'seulement fluoroscopie', 'montrer fluoroscopie'], action: 'modality_RF' },
  { phrases: ['examens endoscopie', 'seulement endoscopie', 'montrer endoscopie'], action: 'modality_ES' },
  // Commandes de focus de champ
  { phrases: ['commentaire deux points', 'commentaire:', 'commentaire', 'au commentaire', 'champ commentaire', 'entrer commentaire'], action: 'focus_comment' },
  { phrases: ['protocole deux points', 'protocole:', 'protocole','protocoles', 'au protocole', 'champ protocole', 'protocole examen'], action: 'focus_protocol' },
  // Commandes de dialogue
  { phrases: ['ouvrir protocoles', 'montrer protocoles', 'dialogue protocoles', 'protocoles scan', 'sélection protocole', 'ouvrir dialogue protocole'], action: 'open_protocol_dialog' },
  { phrases: ['fermer dialogue', 'fermer', 'annuler', 'fermer fenêtre'], action: 'close_dialog' }
];

/**
 * Permanente Targets die IMMER in der Grammatik sein sollen
 * (AppBar-Buttons, die auf jeder Seite sichtbar sind)
 * Format: { id: 'elementId', labels: ['Label1', 'label2', ...] }
 */
const PERMANENT_TARGETS_DE = [
  { id: 'reportingModuleTab', labels: ['Reporting', 'Berichtswesen', 'Befundung', 'Befunde'] },
  { id: 'dashboardButton', labels: ['Dashboard', 'ReportDashboard', 'Report Dashboard', 'Startseite', 'Home', 'Start', 'Hauptseite', 'Übersicht'] },
  { id: 'approvalButton', labels: ['Indikationsprüfung', 'Freigabe', 'Studien freigeben', 'Freigaben'] },
  { id: 'myTasksButton', labels: ['Meine Aufgaben', 'Aufgaben', 'Offene Befunde', 'Meine Befunde'] },
  { id: 'teamTasksButton', labels: ['Team-Aufgaben', 'Team Aufgaben', 'Team', 'Teamaufgaben'] },
  { id: 'chatButton', labels: ['Chat', 'Nachrichten', 'Mitteilungen'] },
  { id: 'calendarButton', labels: ['Kalender', 'Terminkalender', 'Termine'] },
  { id: 'searchButton', labels: ['Suche', 'Suchen'] },
  { id: 'menuButton', labels: ['Menü', 'Menu', 'Einstellungen'] },
  { id: 'profileButton', labels: ['Profil', 'Mein Profil', 'Benutzerprofil'] },
  { id: 'adminButton', labels: ['Admin', 'Administration', 'Verwaltung'] },
  { id: 'statisticsButton', labels: ['Statistik', 'Statistiken'] },
  // Dashboard Cards
  { id: 'dashboardCard_approval', labels: ['Studien freizugeben', 'Freizugebende Studien'] },
  { id: 'dashboardCard_myTasks', labels: ['Offene Befunde', 'Meine Befunde'] },
  { id: 'dashboardCard_teamTasks', labels: ['Team-Aufgaben', 'Offene Team-Befunde'] },
  { id: 'dashboardCard_chat', labels: ['Ungelesene Nachrichten', 'Chat'] }
];

const PERMANENT_TARGETS_EN = [
  { id: 'reportingModuleTab', labels: ['Reporting', 'Reports'] },
  { id: 'dashboardButton', labels: ['Dashboard', 'Report Dashboard', 'Home', 'Home screen', 'Homescreen', 'Start', 'Main page', 'Overview'] },
  { id: 'approvalButton', labels: ['Approval', 'Indication Check', 'Approvals', 'Release'] },
  { id: 'myTasksButton', labels: ['My Tasks', 'Tasks', 'Open Reports', 'My Reports'] },
  { id: 'teamTasksButton', labels: ['Team Tasks', 'Team'] },
  { id: 'chatButton', labels: ['Chat', 'Messages'] },
  { id: 'calendarButton', labels: ['Calendar', 'Appointments'] },
  { id: 'searchButton', labels: ['Search'] },
  { id: 'menuButton', labels: ['Menu', 'Settings'] },
  { id: 'profileButton', labels: ['Profile', 'My Profile'] },
  { id: 'adminButton', labels: ['Admin', 'Administration'] },
  { id: 'statisticsButton', labels: ['Statistics'] },
  { id: 'dashboardCard_approval', labels: ['Studies to Release'] },
  { id: 'dashboardCard_myTasks', labels: ['Open Reports'] },
  { id: 'dashboardCard_teamTasks', labels: ['Team Tasks'] },
  { id: 'dashboardCard_chat', labels: ['Unread Messages'] }
];

const PERMANENT_TARGETS_FR = [
  { id: 'reportingModuleTab', labels: ['Rapports', 'Reporting', 'Compte rendu'] },
  { id: 'dashboardButton', labels: ['Tableau de bord', 'Dashboard', 'Accueil', 'Page principale', 'Démarrer', 'Aperçu'] },
  { id: 'approvalButton', labels: ['Approbation', 'Vérification', 'Approbations', 'Libérer'] },
  { id: 'myTasksButton', labels: ['Mes tâches', 'Tâches', 'Mes rapports'] },
  { id: 'teamTasksButton', labels: ['Tâches d\'équipe', 'Équipe'] },
  { id: 'chatButton', labels: ['Chat', 'Messages'] },
  { id: 'calendarButton', labels: ['Calendrier', 'Rendez-vous'] },
  { id: 'searchButton', labels: ['Recherche', 'Chercher'] },
  { id: 'menuButton', labels: ['Menu', 'Paramètres'] },
  { id: 'profileButton', labels: ['Profil', 'Mon profil'] },
  { id: 'adminButton', labels: ['Admin', 'Administration'] },
  { id: 'statisticsButton', labels: ['Statistiques'] },
  { id: 'dashboardCard_approval', labels: ['Études à libérer'] },
  { id: 'dashboardCard_myTasks', labels: ['Rapports ouverts'] },
  { id: 'dashboardCard_teamTasks', labels: ['Tâches d\'équipe'] },
  { id: 'dashboardCard_chat', labels: ['Messages non lus'] }
];

// ============================================================================
// HILFSFUNKTIONEN
// ============================================================================

/**
 * Extrahiert den sichtbaren Text eines Elements
 * Berücksichtigt verschiedene Quellen für den Label-Text
 */
function extractElementLabel(element) {
  // 1. Explizites voice-label Attribut
  if (element.dataset.voiceLabel) {
    return element.dataset.voiceLabel;
  }
  
  // 2. aria-label
  if (element.getAttribute('aria-label')) {
    return element.getAttribute('aria-label');
  }
  
  // 3. title Attribut
  if (element.title) {
    return element.title;
  }
  
  // 4. Text-Content (nur direkter Text, keine verschachtelten)
  let textContent = '';
  
  // Für MUI DataGrid Zeilen
  if (element.classList.contains('MuiDataGrid-row')) {
    // Suche nach patient_name Zelle (MUI DataGrid Format)
    const patientNameCell = element.querySelector('[data-field="patient_name"]');
    if (patientNameCell) {
      // MUI DataGrid hat den Text oft in einem verschachtelten Element
      const textEl = patientNameCell.querySelector('.MuiTypography-root') || patientNameCell;
      textContent = textEl.textContent?.trim() || '';
      // Bei mehrzeiligen Zellen nur erste Zeile (Name)
      if (textContent.includes('\n')) {
        textContent = textContent.split('\n')[0].trim();
      }
      // Entferne "ID: xxx" falls vorhanden
      textContent = textContent.replace(/ID:\s*\S+/g, '').trim();
    }
    
    // Fallback: Suche nach anderen Namens-Feldern
    if (!textContent) {
      const nameCell = element.querySelector('[data-field="name"], [data-field="patientName"]');
      if (nameCell) {
        textContent = nameCell.textContent?.trim() || '';
      }
    }
  }
  // Für normale Tabellenzeilen
  else if (element.tagName === 'TR' || element.getAttribute('role') === 'row') {
    // Suche nach Name-Spalte
    const nameCell = element.querySelector('[data-field="patientName"], [data-field="patient_name"], [data-field="name"], .patient-name, .name-column');
    if (nameCell) {
      textContent = nameCell.textContent?.trim() || '';
    }
    // Fallback: Erste Zelle mit Text
    if (!textContent) {
      const firstTextCell = element.querySelector('td, [role="cell"]');
      if (firstTextCell) {
        textContent = firstTextCell.textContent?.trim() || '';
      }
    }
  } else {
    // Für normale Elemente: Direkten Text extrahieren
    // Ignoriere versteckte Elemente und SVGs
    const clone = element.cloneNode(true);
    clone.querySelectorAll('svg, [aria-hidden="true"], .sr-only, style, script').forEach(el => el.remove());
    textContent = clone.textContent?.trim() || '';
  }
  
  // 5. value Attribut (für Inputs)
  if (!textContent && element.value) {
    textContent = element.value;
  }
  
  // Bereinigen: Mehrfache Leerzeichen entfernen
  textContent = textContent.replace(/\s+/g, ' ').trim();
  
  // Maximale Länge begrenzen (für JSGF Kompatibilität)
  if (textContent.length > 50) {
    textContent = textContent.substring(0, 50);
  }
  
  return textContent;
}

/**
 * Generiert eine eindeutige ID für ein Element ohne ID
 */
function generateElementId(element, index) {
  const tag = element.tagName.toLowerCase();
  const role = element.getAttribute('role') || '';
  const label = extractElementLabel(element);
  
  // Versuche eine sinnvolle ID zu generieren
  if (label) {
    // Konvertiere Label zu gültiger ID
    const safeLabel = label
      .toLowerCase()
      .replace(/[äöü]/g, match => ({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue' }[match]))
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 30);
    return `voice_${tag}_${safeLabel}_${index}`;
  }
  
  return `voice_${tag}_${role || 'element'}_${index}`;
}

/**
 * Prüft ob ein Element sichtbar ist (oder explizit als Voice-Target markiert)
 */
function isElementVisible(element) {
  if (!element) return false;
  
  // WICHTIG: Elemente mit data-voice-label sind IMMER Voice-Targets,
  // auch wenn sie versteckt sind (z.B. vorgeladene Dialog-Elemente)
  if (element.dataset.voiceLabel) {
    return true;
  }
  
  // Prüfe computed style
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  
  // Prüfe Bounding Box
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }
  
  // Prüfe ob im Viewport (optional - könnte auch ausgeblendete Elemente erlauben)
  // Wir erlauben auch Elemente außerhalb des Viewports (z.B. in Drawers)
  
  return true;
}

/**
 * Setzt den Inaktivitätstracker zurück
 * Verhindert Auto-Logout während des Diktierens mit Sprachbefehlen
 */
function resetInactivityTracker() {
  try {
    // Aktualisiere lastActivity im sessionStorage (wie sessionService.resetActivity())
    sessionStorage.setItem('lastActivity', Date.now().toString());
    console.log('[FluencyDirect] ⏱️ Inaktivitätstracker zurückgesetzt');
  } catch (e) {
    // Fehler ignorieren, falls sessionStorage nicht verfügbar
    console.warn('[FluencyDirect] Konnte Inaktivitätstracker nicht zurücksetzen:', e);
  }
}

/**
 * Escapet einen String für JSGF-Grammatik
 * JSGF verwendet ISO8859-1, also müssen Sonderzeichen behandelt werden
 */
function escapeForJSGF(text) {
  if (!text) return '';
  
  return text
    // Entferne problematische Zeichen für JSGF
    .replace(/[{}[\]|;#<>()]/g, '')
    // Behalte deutsche Umlaute (werden von Fluency Direct unterstützt)
    .trim();
}

/**
 * Generiert Varianten eines Labels für bessere Erkennung
 */
function generateLabelVariants(label) {
  if (!label) return [];
  
  const variants = new Set();
  variants.add(label);
  
  // Lowercase Variante
  variants.add(label.toLowerCase());
  
  // Ohne führende/nachfolgende Artikel
  const withoutArticles = label.replace(/^(der|die|das|ein|eine|the|a|an)\s+/i, '');
  if (withoutArticles !== label) {
    variants.add(withoutArticles);
  }
  
  // Ohne führende Präfixe wie "Region", "Protokoll" etc. für Dialog-Elemente
  const withoutPrefix = label.replace(/^(Region|Protokoll|Protocol|Région)\s+/i, '');
  if (withoutPrefix !== label) {
    variants.add(withoutPrefix);
    variants.add(withoutPrefix.toLowerCase());
  }
  
  // Für zusammengesetzte Wörter: Auch einzelne Teile
  if (label.includes('-')) {
    label.split('-').forEach(part => {
      if (part.length > 2) variants.add(part.trim());
    });
  }
  
  // Für mehrteilige Namen: Nachname allein
  const nameParts = label.split(/\s+/);
  if (nameParts.length >= 2) {
    // Letzter Teil (Nachname)
    variants.add(nameParts[nameParts.length - 1]);
    // Erster Teil (Vorname)
    variants.add(nameParts[0]);
  }
  
  return Array.from(variants).filter(v => v && v.length > 1);
}

// ============================================================================
// HAUPTFUNKTIONEN
// ============================================================================

/**
 * Scannt das DOM nach steuerbaren Elementen
 * @returns {Array} Array von {id, label, variants, element, isTableRow}
 */
function scanVoiceTargets() {
  const targets = [];
  const seenIds = new Set();
  let autoIndex = 0;
  
  // Sammle alle potenziell steuerbaren Elemente
  const selector = CLICKABLE_SELECTORS.join(', ');
  const elements = document.querySelectorAll(selector);
  
  elements.forEach(element => {
    // Überspringe unsichtbare Elemente
    if (!isElementVisible(element)) {
      return;
    }
    
    // Überspringe Elemente ohne sinnvollen Text
    const label = extractElementLabel(element);
    if (!label || label.length < 2) {
      return;
    }
    
    // Spezialbehandlung für MUI DataGrid Zeilen
    const isDataGridRow = element.classList.contains('MuiDataGrid-row');
    
    // ID ermitteln oder generieren
    let id = element.id;
    
    // MUI DataGrid: Verwende data-id Attribut
    if (!id && isDataGridRow) {
      const dataId = element.getAttribute('data-id');
      if (dataId) {
        id = `row_${dataId}`;
        element.setAttribute('data-voice-id', id);
      }
    }
    
    if (!id) {
      id = generateElementId(element, autoIndex++);
      // Temporär ID setzen für den Klick
      element.setAttribute('data-voice-id', id);
    }
    
    // Duplikate überspringen
    if (seenIds.has(id)) {
      return;
    }
    seenIds.add(id);
    
    // Varianten für bessere Erkennung generieren
    const variants = generateLabelVariants(label);
    
    targets.push({
      id,
      label,
      variants,
      element,
      tagName: element.tagName,
      role: element.getAttribute('role') || '',
      isTableRow: isDataGridRow || element.tagName === 'TR'
    });
  });
  
  return targets;
}

/**
 * Findet alle DataGrid-Zeilen auf der Seite
 * @returns {Array} Array von DataGrid Row Elements
 */
function getDataGridRows() {
  // Finde das ERSTE/HAUPTSÄCHLICHE DataGrid auf der Seite (oberste Tabelle)
  // Ignoriere Voruntersuchungen und andere sekundäre Tabellen
  const dataGridContainers = document.querySelectorAll('.MuiDataGrid-root');
  
  if (dataGridContainers.length === 0) {
    console.log('[FluencyDirect] Kein DataGrid gefunden');
    return [];
  }
  
  // Nimm das erste DataGrid (Haupttabelle) - sortiert nach Y-Position
  const sortedContainers = Array.from(dataGridContainers).sort((a, b) => {
    const rectA = a.getBoundingClientRect();
    const rectB = b.getBoundingClientRect();
    return rectA.top - rectB.top;
  });
  
  const mainGrid = sortedContainers[0];
  const rows = Array.from(mainGrid.querySelectorAll('.MuiDataGrid-row'));
  
  console.log(`[FluencyDirect] Haupttabelle gefunden mit ${rows.length} Zeilen (von ${dataGridContainers.length} DataGrids)`);
  
  // Sortiere nach data-rowindex für korrekte visuelle Reihenfolge
  rows.sort((a, b) => {
    const indexA = parseInt(a.getAttribute('data-rowindex') || '0', 10);
    const indexB = parseInt(b.getAttribute('data-rowindex') || '0', 10);
    return indexA - indexB;
  });
  
  return rows;
}

/**
 * Findet die aktuell ausgewählte DataGrid-Zeile
 * @returns {Element|null} Die ausgewählte Zeile oder null
 */
function getSelectedDataGridRow() {
  return document.querySelector('.MuiDataGrid-row.Mui-selected');
}

/**
 * Navigiert zur nächsten/vorherigen/ersten/letzten Zeile
 * @param {string} direction - 'next', 'previous', 'first', 'last'
 */
function navigateTableRow(direction) {
  const rows = getDataGridRows();
  if (rows.length === 0) {
    console.log('[FluencyDirect] Keine Tabellenzeilen gefunden');
    return false;
  }
  
  const selectedRow = getSelectedDataGridRow();
  let targetRow = null;
  
  if (direction === 'first') {
    targetRow = rows[0];
  } else if (direction === 'last') {
    targetRow = rows[rows.length - 1];
  } else if (selectedRow) {
    const currentIndex = rows.indexOf(selectedRow);
    
    if (direction === 'next') {
      targetRow = rows[Math.min(currentIndex + 1, rows.length - 1)];
    } else if (direction === 'previous') {
      targetRow = rows[Math.max(currentIndex - 1, 0)];
    }
  } else {
    // Keine Zeile ausgewählt: Erste/Letzte nehmen
    targetRow = direction === 'next' ? rows[0] : rows[rows.length - 1];
  }
  
  if (targetRow) {
    clickTableRow(targetRow);
    return true;
  }
  
  return false;
}

/**
 * Wählt einen Patienten nach Index (0-basiert) aus
 * @param {number} index - 0-basierter Index der Zeile
 */
function selectPatientByIndex(index) {
  const rows = getDataGridRows();
  
  if (rows.length === 0) {
    console.log('[FluencyDirect] Keine Tabellenzeilen gefunden');
    return false;
  }
  
  if (index < 0 || index >= rows.length) {
    console.log(`[FluencyDirect] Patient ${index + 1} nicht vorhanden (nur ${rows.length} Patienten)`);
    return false;
  }
  
  const targetRow = rows[index];
  console.log(`[FluencyDirect] Wähle Patient ${index + 1} von ${rows.length}`);
  
  clickTableRow(targetRow);
  return true;
}

/**
 * Klickt auf eine Tabellenzeile mit visuellem Feedback
 * @param {Element} row - Die zu klickende Zeile
 */
function clickTableRow(row) {
  if (!row) return;
  
  const rowIndex = row.getAttribute('data-rowindex');
  const dataId = row.getAttribute('data-id');
  console.log(`[FluencyDirect] Klicke auf Zeile: visuell #${parseInt(rowIndex) + 1} (data-id: ${dataId})`);
  
  // Finde eine klickbare Zelle innerhalb der Zeile (erste Zelle die nicht leer ist)
  const cell = row.querySelector('.MuiDataGrid-cell:not(.MuiDataGrid-cellEmpty)');
  const clickTarget = cell || row;
  
  console.log(`[FluencyDirect] Klick-Ziel: ${clickTarget.tagName}.${clickTarget.className.split(' ')[0]}`);
  
  // Visuelles Feedback
  row.style.outline = '3px solid var(--rd-success)';
  row.style.outlineOffset = '2px';
  setTimeout(() => {
    row.style.outline = '';
    row.style.outlineOffset = '';
  }, 500);
  
  // Scroll zur Zeile wenn nötig
  row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  
  // Schritt 1: Einfacher Klick um die Zeile auszuwählen
  clickTarget.dispatchEvent(new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: clickTarget.getBoundingClientRect().left + 10,
    clientY: clickTarget.getBoundingClientRect().top + 10
  }));
  
  // Schritt 2: Doppelklick um den Report zu öffnen (nach kurzer Verzögerung)
  setTimeout(() => {
    console.log(`[FluencyDirect] Doppelklick auf Zeile ${dataId}`);
    clickTarget.dispatchEvent(new MouseEvent('dblclick', {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: clickTarget.getBoundingClientRect().left + 10,
      clientY: clickTarget.getBoundingClientRect().top + 10
    }));
  }, 150);
}

/**
 * Setzt den Status-Filter über ein Custom Event
 * @param {string} filterValue - Der Filterwert ('all', 'NEW', 'APPROVED', 'REJECTED')
 */
function triggerStatusFilter(filterValue) {
  console.log(`[FluencyDirect] 🔍 Setze Status-Filter: ${filterValue}`);
  
  // Visuelles Feedback
  const filterLabels = {
    'all': 'Alle',
    'NEW': 'Neu',
    'APPROVED': 'Genehmigt',
    'REJECTED': 'Abgelehnt'
  };
  console.log(`[FluencyDirect] ✅ Filter wird gesetzt auf: ${filterLabels[filterValue] || filterValue}`);
  
  // Dispatch Custom Event für React-Komponenten
  const event = new CustomEvent('voiceSetStatusFilter', {
    detail: { filter: filterValue }
  });
  console.log('[FluencyDirect] 📤 Event dispatched:', event);
  window.dispatchEvent(event);
}

/**
 * Triggert die Vorbefund-Suche über ein Custom Event
 * @param {string} action - 'last', 'first', 'next', 'previous'
 */
function triggerPriorStudySearch(action) {
  const actionLabels = {
    'last': 'Letzter passender Vorbefund',
    'first': 'Erster/Ältester Vorbefund',
    'next': 'Nächster Vorbefund',
    'previous': 'Vorheriger Vorbefund'
  };
  
  console.log(`[FluencyDirect] 📋 Vorbefund-Suche: ${actionLabels[action] || action}`);
  
  // Dispatch Custom Event für React-Komponenten
  const event = new CustomEvent('voicePriorStudySearch', {
    detail: { action: action }
  });
  console.log('[FluencyDirect] 📤 Vorbefund Event dispatched:', event);
  window.dispatchEvent(event);
}

/**
 * Fokussiert ein Eingabefeld per Voice Command
 * @param {string} fieldName - Name des Feldes ('comment', 'protocol')
 */
function triggerFieldFocus(fieldName) {
  const fieldLabels = {
    'comment': 'Kommentarfeld',
    'protocol': 'Protokollfeld'
  };
  
  console.log(`[FluencyDirect] 📝 Fokussiere Feld: ${fieldLabels[fieldName] || fieldName}`);
  
  // Dispatch Custom Event für React-Komponenten
  const event = new CustomEvent('voiceFieldFocus', {
    detail: { field: fieldName }
  });
  console.log('[FluencyDirect] 📤 Field Focus Event dispatched:', event);
  window.dispatchEvent(event);
}

/**
 * Löst Dialog-Aktionen per Voice Command aus
 * @param {string} action - Aktion ('openProtocolDialog', 'closeDialog', 'selectRegion', 'selectProtocol')
 * @param {string} [value] - Optionaler Wert (z.B. Region-Name oder Protokoll-Name)
 */
function triggerDialogAction(action, value = null) {
  const actionLabels = {
    'openProtocolDialog': 'Protokoll-Dialog öffnen',
    'closeDialog': 'Dialog schließen',
    'selectRegion': `Region auswählen: ${value}`,
    'selectProtocol': `Protokoll auswählen: ${value}`
  };
  
  console.log(`[FluencyDirect] 🗂️ Dialog-Aktion: ${actionLabels[action] || action}`);
  
  // Dispatch Custom Event für React-Komponenten
  const event = new CustomEvent('voiceDialogAction', {
    detail: { action, value }
  });
  console.log('[FluencyDirect] 📤 Dialog Action Event dispatched:', event);
  window.dispatchEvent(event);
}

/**
 * Berechnet ein Datum relativ zu heute
 * @param {string} relative - 'today', 'yesterday', 'tomorrow', oder 'clear'
 * @returns {string} Datum im Format YYYY-MM-DD oder '' für clear
 */
function getRelativeDate(relative) {
  const today = new Date();
  let targetDate;
  
  switch (relative) {
    case 'today':
      targetDate = today;
      break;
    case 'yesterday':
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() - 1);
      break;
    case 'tomorrow':
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() + 1);
      break;
    case 'clear':
      return '';
    default:
      return '';
  }
  
  // Format: YYYY-MM-DD
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, '0');
  const day = String(targetDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Setzt den Datums-Filter über ein Custom Event
 * @param {string} relative - 'today', 'yesterday', 'tomorrow', oder 'clear'
 */
function triggerDateFilter(relative) {
  const dateValue = getRelativeDate(relative);
  
  const dateLabels = {
    'today': 'Heute',
    'yesterday': 'Gestern',
    'tomorrow': 'Morgen',
    'clear': 'Kein Datum'
  };
  
  console.log(`[FluencyDirect] 📅 Setze Datums-Filter: ${dateLabels[relative] || relative} (${dateValue || 'leer'})`);
  
  // Dispatch Custom Event für React-Komponenten
  const event = new CustomEvent('voiceSetDateFilter', {
    detail: { date: dateValue, relative: relative }
  });
  console.log('[FluencyDirect] 📤 Datum Event dispatched:', event);
  window.dispatchEvent(event);
}

/**
 * Mapping von Sprachbefehlen zu DICOM-Modalitäts-Kürzeln
 * Einige Modalitäten haben alternative Codes (z.B. Röntgen kann CR oder DX sein)
 */
const MODALITY_VOICE_MAPPING = {
  'CT': 'CT',           // Computertomographie
  'MR': 'MR',           // Magnetresonanztomographie
  'DX': 'DX',           // Digitale Radiographie (Röntgen)
  'CR': 'CR',           // Computed Radiography (auch Röntgen)
  'US': 'US',           // Ultraschall
  'XA': 'XA',           // Angiographie
  'MG': 'MG',           // Mammographie
  'NM': 'NM',           // Nuklearmedizin
  'PT': 'PT',           // PET
  'RF': 'RF',           // Fluoroskopie
  'ES': 'ES'            // Endoskopie
};

/**
 * Setzt den Modalitäten-Filter über ein Custom Event
 * @param {string} modalityCode - Das Modalitäts-Kürzel ('all', 'CT', 'MR', etc.)
 */
function triggerModalityFilter(modalityCode) {
  console.log(`[FluencyDirect] 🏥 Setze Modalitäten-Filter: ${modalityCode}`);
  
  // Modalitätsnamen für Log
  const modalityNames = {
    'all': 'Alle Modalitäten',
    'CT': 'CT (Computertomographie)',
    'MR': 'MRT (Magnetresonanz)',
    'DX': 'Röntgen (Digital)',
    'CR': 'Röntgen (Computed)',
    'US': 'Ultraschall',
    'XA': 'Angiographie',
    'MG': 'Mammographie',
    'NM': 'Nuklearmedizin',
    'PT': 'PET',
    'RF': 'Fluoroskopie',
    'ES': 'Endoskopie'
  };
  console.log(`[FluencyDirect] ✅ Modalität: ${modalityNames[modalityCode] || modalityCode}`);
  
  // Dispatch Custom Event für React-Komponenten
  const event = new CustomEvent('voiceSetModalityFilter', {
    detail: { modality: modalityCode }
  });
  console.log('[FluencyDirect] 📤 Modalität Event dispatched:', event);
  window.dispatchEvent(event);
}

/**
 * Generiert die JSGF-Grammatik basierend auf aktuellen DOM-Elementen
 * @returns {string} JSGF-Grammatik-String
 */
function generateGrammar() {
  const targets = scanVoiceTargets();
  
  // Aktuelle Sprache ermitteln
  const lang = (localStorage.getItem('i18nextLng') || 'de').split('-')[0];
  
  // Aktionsverben, spezielle Befehle und permanente Targets basierend auf Sprache
  let actionVerbs;
  let specialCommands;
  let permanentTargets;
  switch (lang) {
    case 'en':
      actionVerbs = ACTION_VERBS_EN;
      specialCommands = SPECIAL_COMMANDS_EN;
      permanentTargets = PERMANENT_TARGETS_EN;
      break;
    case 'fr':
      actionVerbs = ACTION_VERBS_FR;
      specialCommands = SPECIAL_COMMANDS_FR;
      permanentTargets = PERMANENT_TARGETS_FR;
      break;
    default:
      actionVerbs = ACTION_VERBS_DE;
      specialCommands = SPECIAL_COMMANDS_DE;
      permanentTargets = PERMANENT_TARGETS_DE;
  }
  
  // Sammle alle Target-IDs die bereits im DOM gefunden wurden
  const existingTargetsMap = new Map(targets.map(t => [t.id, t]));
  
  // Füge permanente Targets hinzu oder erweitere existierende um die permanenten Labels
  permanentTargets.forEach(pt => {
    const existingTarget = existingTargetsMap.get(pt.id);
    if (existingTarget) {
      // Element existiert bereits - füge permanente Labels zu den Varianten hinzu
      const existingVariants = new Set(existingTarget.variants);
      pt.labels.forEach(label => {
        existingVariants.add(label);
        existingVariants.add(label.toLowerCase());
      });
      existingTarget.variants = Array.from(existingVariants);
    } else {
      // Element existiert nicht - füge als permanentes Target hinzu
      targets.push({
        id: pt.id,
        label: pt.labels[0],
        variants: pt.labels,
        isPermanent: true
      });
    }
  });
  
  // JSGF Header
  let grammar = `#JSGF V1.0 ASCII ${lang};\n`;
  grammar += `grammar ReportingDashboard;\n\n`;
  
  // Hauptregel - WICHTIG: Reihenfolge von spezifisch zu allgemein:
  // 1. specialCommand: "zeige neue Untersuchungen", "nächster Patient"
  // 2. action + target: "öffne Menü", "zeige Kalender"  
  // 3. dialogPrefix + target: "Region Kopf", "Protokoll CCT"
  // 4. target alleine: "obere Extremitäten", "CCT nativ" (für Dialog-Elemente)
  grammar += `public <command> = <NULL> { var commandId = 'UNDEFINED'; } `;
  grammar += `( <specialCommand> | <action> [das | die | den | der] <target> | <dialogPrefix> <target> | <target> | <NULL> ) { this.commandId = commandId };\n\n`;
  
  // Aktionsverben-Regel
  grammar += `<action> = `;
  const allActions = actionVerbs.map(group => group.join(' | ')).join(' | ');
  grammar += allActions + `;\n\n`;
  
  // Dialog-Präfixe (Region, Protokoll) - erlaubt "Region Kopf" oder "Protokoll CCT" ohne Aktionsverb
  let dialogPrefixes;
  switch (lang) {
    case 'en':
      dialogPrefixes = ['Region', 'region', 'Protocol', 'protocol'];
      break;
    case 'fr':
      dialogPrefixes = ['Région', 'région', 'Region', 'region', 'Protocole', 'protocole'];
      break;
    default:
      dialogPrefixes = ['Region', 'region', 'Protokoll', 'protokoll'];
  }
  grammar += `<dialogPrefix> = ${dialogPrefixes.join(' | ')};\n\n`;
  
  // Spezielle Befehle (nächster Patient, etc.)
  grammar += `<specialCommand> = \n`;
  const specialRules = specialCommands.map(cmd => {
    const phrasesStr = cmd.phrases.map(p => escapeForJSGF(p)).join(' | ');
    return `  ( ${phrasesStr} ) { commandId='SPECIAL:${cmd.action}' }`;
  });
  grammar += specialRules.join(' |\n');
  grammar += `;\n\n`;
  
  // Target-Regel mit allen gefundenen Elementen
  if (targets.length > 0) {
    grammar += `<target> = \n`;
    
    const targetRules = targets.map(target => {
      const escapedVariants = target.variants
        .map(v => escapeForJSGF(v))
        .filter(v => v && v.length > 1);
      
      if (escapedVariants.length === 0) {
        return null;
      }
      
      const variantString = escapedVariants.join(' | ');
      return `  ( ${variantString} ) { commandId='${target.id}' }`;
    }).filter(rule => rule !== null);
    
    grammar += targetRules.join(' |\n');
    grammar += `;\n`;
  } else {
    // Fallback wenn keine Targets gefunden
    grammar += `<target> = nichts { commandId='UNDEFINED' };\n`;
  }
  
  return grammar;
}

/**
 * Führt einen Sprachbefehl aus (klickt das Element)
 * @param {string|object} commandIdOrObject - Die ID des zu klickenden Elements oder Fluency Direct Objekt
 */
function executeCommand(commandIdOrObject) {
  console.log('[FluencyDirect] 🎤 executeCommand aufgerufen mit:', commandIdOrObject);
  
  // ⏱️ Inaktivitätstracker zurücksetzen bei jedem Sprachbefehl
  // Verhindert Auto-Logout während des Diktierens
  resetInactivityTracker();
  
  // Fluency Direct übergibt manchmal ein Objekt mit commandId Property
  let commandId;
  if (typeof commandIdOrObject === 'object' && commandIdOrObject !== null) {
    commandId = commandIdOrObject.commandId;
    console.log('[FluencyDirect] 📦 Objekt erkannt, extrahiere commandId:', commandId);
    console.log('[FluencyDirect] 🗣️ Gesprochener Text:', commandIdOrObject.$value || commandIdOrObject.value || '');
  } else {
    commandId = commandIdOrObject;
  }
  
  if (!commandId || commandId === 'UNDEFINED' || commandId === '') {
    console.warn('[FluencyDirect] Ungültige oder leere commandId');
    return;
  }
  
  // Spezielle Befehle behandeln
  if (commandId.startsWith('SPECIAL:')) {
    const action = commandId.replace('SPECIAL:', '');
    console.log('[FluencyDirect] ⚡ Spezieller Befehl:', action);
    
    // Prüfe auf nummerierte Patienten (patient_1 bis patient_10)
    const patientMatch = action.match(/^patient_(\d+)$/);
    if (patientMatch) {
      const patientIndex = parseInt(patientMatch[1], 10) - 1; // 0-basiert
      selectPatientByIndex(patientIndex);
      playFeedbackSound('success');
      return;
    }
    
    switch (action) {
      case 'nextPatient':
        navigateTableRow('next');
        playFeedbackSound('success');
        return;
      case 'previousPatient':
        navigateTableRow('previous');
        playFeedbackSound('success');
        return;
      case 'firstPatient':
        navigateTableRow('first');
        playFeedbackSound('success');
        return;
      case 'lastPatient':
        navigateTableRow('last');
        playFeedbackSound('success');
        return;
      // Status-Filter Befehle
      case 'filter_all':
        triggerStatusFilter('all');
        playFeedbackSound('success');
        return;
      case 'filter_new':
        triggerStatusFilter('NEW');
        playFeedbackSound('success');
        return;
      case 'filter_approved':
        triggerStatusFilter('APPROVED');
        playFeedbackSound('success');
        return;
      case 'filter_rejected':
        triggerStatusFilter('REJECTED');
        playFeedbackSound('success');
        return;
      // Vorbefund-Suche Befehle
      case 'prior_study_last':
        triggerPriorStudySearch('last');
        playFeedbackSound('success');
        return;
      case 'prior_study_first':
        triggerPriorStudySearch('first');
        playFeedbackSound('success');
        return;
      case 'prior_study_next':
        triggerPriorStudySearch('next');
        playFeedbackSound('success');
        return;
      case 'prior_study_previous':
        triggerPriorStudySearch('previous');
        playFeedbackSound('success');
        return;
      // Datums-Filter Befehle
      case 'date_today':
        triggerDateFilter('today');
        playFeedbackSound('success');
        return;
      case 'date_yesterday':
        triggerDateFilter('yesterday');
        playFeedbackSound('success');
        return;
      case 'date_tomorrow':
        triggerDateFilter('tomorrow');
        playFeedbackSound('success');
        return;
      case 'date_clear':
        triggerDateFilter('clear');
        playFeedbackSound('success');
        return;
      // Modalitäten-Filter Befehle
      case 'modality_all':
        triggerModalityFilter('all');
        playFeedbackSound('success');
        return;
      case 'modality_CT':
        triggerModalityFilter('CT');
        playFeedbackSound('success');
        return;
      case 'modality_MR':
        triggerModalityFilter('MR');
        playFeedbackSound('success');
        return;
      case 'modality_DX':
        triggerModalityFilter('DX');
        playFeedbackSound('success');
        return;
      case 'modality_US':
        triggerModalityFilter('US');
        playFeedbackSound('success');
        return;
      case 'modality_XA':
        triggerModalityFilter('XA');
        playFeedbackSound('success');
        return;
      case 'modality_MG':
        triggerModalityFilter('MG');
        playFeedbackSound('success');
        return;
      case 'modality_NM':
        triggerModalityFilter('NM');
        playFeedbackSound('success');
        return;
      case 'modality_PT':
        triggerModalityFilter('PT');
        playFeedbackSound('success');
        return;
      case 'modality_RF':
        triggerModalityFilter('RF');
        playFeedbackSound('success');
        return;
      case 'modality_ES':
        triggerModalityFilter('ES');
        playFeedbackSound('success');
        return;
      // Feld-Fokus Befehle
      case 'focus_comment':
        triggerFieldFocus('comment');
        playFeedbackSound('success');
        return;
      case 'focus_protocol':
        triggerFieldFocus('protocol');
        playFeedbackSound('success');
        return;
      // Dialog-Befehle
      case 'open_protocol_dialog':
        triggerDialogAction('openProtocolDialog');
        playFeedbackSound('success');
        return;
      case 'close_dialog':
        triggerDialogAction('closeDialog');
        playFeedbackSound('success');
        return;
      default:
        console.warn('[FluencyDirect] Unbekannter Spezialbefehl:', action);
        return;
    }
  }
  
  // Mapping von Button-IDs zu Routen (für Fälle wo Element nicht im DOM ist)
  const routeMapping = {
    'homeButton': '/',
    'reportingModuleTab': '/',
    'dashboardButton': '/',
    'approvalButton': '/approval',
    'myTasksButton': '/my-tasks',
    'teamTasksButton': '/team-tasks',
    'chatButton': 'CHAT', // Spezieller Fall
    'calendarButton': '/calendar',
    'searchButton': '/search',
    'profileButton': '/profile',
    'adminButton': '/admin',
    'statisticsButton': '/statistics',
    'dashboardCard_approval': '/approval',
    'dashboardCard_myTasks': '/my-tasks',
    'dashboardCard_teamTasks': '/team-tasks',
    'dashboardCard_chat': 'CHAT'
  };
  
  // Suche Element nach ID oder data-voice-id
  let element = document.getElementById(commandId);
  
  if (!element) {
    element = document.querySelector(`[data-voice-id="${commandId}"]`);
  }
  
  // Fallback: Wenn Element nicht gefunden, aber Route bekannt ist
  if (!element && routeMapping[commandId]) {
    const route = routeMapping[commandId];
    console.log('[FluencyDirect] 🧭 Element nicht im DOM, navigiere direkt zu:', route);
    
    if (route === 'CHAT') {
      // Chat öffnen via globales Event
      window.dispatchEvent(new Event('openChat'));
      playFeedbackSound('success');
      return;
    }
    
    // Navigation via React Router oder window.location
    if (window.location.pathname !== route) {
      // Versuche React Router Navigation
      const navigateEvent = new CustomEvent('voiceNavigate', { detail: { route } });
      window.dispatchEvent(navigateEvent);
      
      // Fallback: Direkte Navigation
      setTimeout(() => {
        if (window.location.pathname !== route) {
          window.location.href = route;
        }
      }, 100);
      
      playFeedbackSound('success');
    } else {
      console.log('[FluencyDirect] Bereits auf dieser Seite:', route);
    }
    return;
  }
  
  if (element) {
    console.log('[FluencyDirect] ✅ Element gefunden, klicke:', commandId, element);
    
    // Visuelles Feedback
    const originalOutline = element.style.outline;
    element.style.outline = '3px solid var(--rd-success)';
    element.style.outlineOffset = '2px';
    
    setTimeout(() => {
      element.style.outline = originalOutline;
      element.style.outlineOffset = '';
    }, 500);
    
    // Prüfe auf spezielle Voice-Action Attribute
    const voiceAction = element.dataset.voiceAction;
    const voiceValue = element.dataset.voiceValue;
    
    console.log('[FluencyDirect] 🔍 Element-Attribute:', {
      id: element.id,
      voiceAction: voiceAction,
      voiceValue: voiceValue,
      voiceLabel: element.dataset.voiceLabel
    });
    
    if (voiceAction) {
      console.log('[FluencyDirect] 🎯 Spezielle Voice-Action gefunden:', voiceAction, '→', voiceValue);
      // Dispatch spezielles Event für Dialog-Aktionen
      triggerDialogAction(voiceAction, voiceValue);
      playFeedbackSound('success');
      return;
    }
    
    // Klick ausführen - verwende MouseEvent für bessere Kompatibilität mit MUI
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: element.getBoundingClientRect().left + 10,
      clientY: element.getBoundingClientRect().top + 10
    });
    element.dispatchEvent(clickEvent);
    console.log('[FluencyDirect] ✓ Befehl via MouseEvent ausgeführt');
    
    // Audio-Feedback (optional)
    playFeedbackSound('success');
    
  } else {
    console.error('[FluencyDirect] ✗ Element nicht gefunden:', commandId);
    console.log('[FluencyDirect] Verfügbare IDs:', 
      Array.from(document.querySelectorAll('[id]')).slice(0, 20).map(el => el.id).join(', '));
    
    playFeedbackSound('error');
  }
}

/**
 * Spielt Feedback-Sound ab
 */
function playFeedbackSound(type) {
  try {
    const soundFile = type === 'success' ? '/sounds/success.mp3' : '/sounds/error.mp3';
    const audio = new Audio(soundFile);
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Ignoriere Fehler wenn Sound nicht verfügbar
    });
  } catch (e) {
    // Ignoriere Audio-Fehler
  }
}

// ============================================================================
// DEBUG-FUNKTIONEN
// ============================================================================

/**
 * Zeigt alle steuerbaren Elemente in der Konsole
 */
function showVoiceTargets() {
  const targets = scanVoiceTargets();
  
  console.log('%c=== 🎤 Voice Targets (Steuerbare Elemente) ===', 
    'font-weight: bold; font-size: 14px; color: #2196F3;');
  console.log(`%cGefunden: ${targets.length} Elemente`, 'color: #4CAF50; font-weight: bold;');
  console.log('');
  
  // Gruppiere nach Typ
  const grouped = {};
  targets.forEach(target => {
    const type = target.role || target.tagName.toLowerCase();
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(target);
  });
  
  Object.entries(grouped).forEach(([type, items]) => {
    console.log(`%c📁 ${type.toUpperCase()} (${items.length})`, 'color: var(--rd-accent); font-weight: bold;');
    items.forEach(item => {
      console.log(`  • "${item.label}" → id="${item.id}"`);
      if (item.variants.length > 1) {
        console.log(`    Varianten: ${item.variants.slice(1).join(', ')}`);
      }
    });
  });
  
  console.log('');
  console.log('%c=== Beispiel-Befehle ===', 'font-weight: bold; color: #9C27B0;');
  
  const examples = targets.slice(0, 5);
  examples.forEach(target => {
    console.log(`  "Öffne ${target.label}"`);
  });
  
  console.log('');
  console.log('%c====================================', 
    'font-weight: bold; font-size: 14px; color: #2196F3;');
  
  return targets;
}

/**
 * Testet einen Sprachbefehl (simuliert)
 */
function testVoiceCommand(spokenText) {
  console.log('%c🎤 Test-Sprachbefehl:', 'color: #2196F3; font-weight: bold;', spokenText);
  
  const targets = scanVoiceTargets();
  const normalizedInput = spokenText.toLowerCase().trim();
  
  // Entferne Aktionsverben
  let searchTerm = normalizedInput;
  ACTION_VERBS_DE.flat().forEach(verb => {
    searchTerm = searchTerm.replace(new RegExp(`^${verb}\\s+`, 'i'), '');
  });
  searchTerm = searchTerm.replace(/^(das|die|den|der)\s+/i, '').trim();
  
  console.log('Suche nach:', searchTerm);
  
  // Finde passendes Target
  const match = targets.find(target => {
    return target.variants.some(v => 
      v.toLowerCase().includes(searchTerm) || 
      searchTerm.includes(v.toLowerCase())
    );
  });
  
  if (match) {
    console.log('%c✅ Gefunden:', 'color: #4CAF50; font-weight: bold;', match.label, '→', match.id);
    console.log('Führe aus: executeCommand("' + match.id + '")');
    executeCommand(match.id);
    return match;
  } else {
    console.log('%c❌ Kein passendes Element gefunden', 'color: #f44336;');
    console.log('Verfügbare Targets:', targets.map(t => t.label).join(', '));
    return null;
  }
}

/**
 * Zeigt die aktuelle Grammatik formatiert an
 */
function showGrammar() {
  const grammar = generateGrammar();
  
  console.log('%c=== 📜 Aktuelle JSGF Grammatik ===', 
    'font-weight: bold; font-size: 14px; color: #2196F3;');
  console.log('%cGröße: ' + grammar.length + ' Zeichen', 'color: #4CAF50;');
  console.log('');
  console.log('%c' + grammar, 'font-family: monospace; color: #666; font-size: 11px;');
  console.log('');
  console.log('%c====================================', 
    'font-weight: bold; font-size: 14px; color: #2196F3;');
  
  return grammar;
}

// ============================================================================
// INITIALISIERUNG
// ============================================================================

/**
 * Initialisiert den FluencyDirect Controller
 * Registriert globale Funktionen und richtet Event-Listener ein
 */
function initFluencyDirect() {
  console.log('[FluencyDirect] 🚀 Initialisiere Controller...');
  
  // Globale Funktionen für Fluency Direct registrieren
  // WICHTIG: Fluency Direct erwartet exakt diese Funktionsnamen!
  window.fdGetGrammar = function() {
    console.log('[FluencyDirect] 📜 fdGetGrammar() aufgerufen');
    const grammar = generateGrammar();
    console.log('[FluencyDirect] Grammatik generiert:', grammar.length, 'Zeichen');
    return grammar;
  };
  
  window.fdExecuteCommand = function(commandId) {
    console.log('[FluencyDirect] ⚡ fdExecuteCommand() aufgerufen mit:', commandId);
    executeCommand(commandId);
  };
  
  // Legacy-Funktionen für Kompatibilität (falls noch verwendet)
  window.getGrammar = window.fdGetGrammar;
  window.executeCommand = window.fdExecuteCommand;
  
  // Debug-Funktionen
  window.showVoiceTargets = showVoiceTargets;
  window.testVoiceCommand = testVoiceCommand;
  window.showGrammar = showGrammar;
  window.reloadGrammar = function() {
    console.log('[FluencyDirect] 🔄 Grammatik neu laden...');
    return window.fdGetGrammar();
  };
  
  // Test-Funktionen für Status-Filter
  window.testFilter = function(filter) {
    console.log('[FluencyDirect] 🧪 Test: Status-Filter setzen auf', filter);
    triggerStatusFilter(filter);
  };
  window.testFilterAll = function() { triggerStatusFilter('all'); };
  window.testFilterNew = function() { triggerStatusFilter('NEW'); };
  window.testFilterApproved = function() { triggerStatusFilter('APPROVED'); };
  window.testFilterRejected = function() { triggerStatusFilter('REJECTED'); };
  
  // Test-Funktionen für Modalitäten-Filter
  window.testModality = function(modality) {
    console.log('[FluencyDirect] 🧪 Test: Modalitäten-Filter setzen auf', modality);
    triggerModalityFilter(modality);
  };
  window.testModalityAll = function() { triggerModalityFilter('all'); };
  window.testModalityCT = function() { triggerModalityFilter('CT'); };
  window.testModalityMR = function() { triggerModalityFilter('MR'); };
  window.testModalityDX = function() { triggerModalityFilter('DX'); };
  window.testModalityUS = function() { triggerModalityFilter('US'); };
  
  // Test-Funktionen für Datums-Filter
  window.testDate = function(relative) {
    console.log('[FluencyDirect] 🧪 Test: Datums-Filter setzen auf', relative);
    triggerDateFilter(relative);
  };
  window.testDateToday = function() { triggerDateFilter('today'); };
  window.testDateYesterday = function() { triggerDateFilter('yesterday'); };
  window.testDateTomorrow = function() { triggerDateFilter('tomorrow'); };
  window.testDateClear = function() { triggerDateFilter('clear'); };
  
  // Test-Funktionen für Vorbefund-Suche
  window.testPriorStudy = function(action) {
    console.log('[FluencyDirect] 🧪 Test: Vorbefund-Suche', action);
    triggerPriorStudySearch(action);
  };
  window.testPriorStudyLast = function() { triggerPriorStudySearch('last'); };
  window.testPriorStudyFirst = function() { triggerPriorStudySearch('first'); };
  window.testPriorStudyNext = function() { triggerPriorStudySearch('next'); };
  window.testPriorStudyPrevious = function() { triggerPriorStudySearch('previous'); };
  
  // Status ausgeben
  console.log('[FluencyDirect] ✅ Controller initialisiert');
  console.log('[FluencyDirect] Verfügbare Funktionen:');
  console.log('  • fdGetGrammar()      - Liefert JSGF-Grammatik');
  console.log('  • fdExecuteCommand(id) - Führt Befehl aus');
  console.log('  • showVoiceTargets()  - Zeigt steuerbare Elemente');
  console.log('  • testVoiceCommand(text) - Testet Sprachbefehl');
  console.log('  • showGrammar()       - Zeigt aktuelle Grammatik');
  
  // Initiale Targets scannen und anzeigen
  const initialTargets = scanVoiceTargets();
  console.log(`[FluencyDirect] 🎯 ${initialTargets.length} steuerbare Elemente gefunden`);
  
  return true;
}

// ============================================================================
// EXPORT
// ============================================================================

export {
  initFluencyDirect,
  generateGrammar,
  executeCommand,
  scanVoiceTargets,
  showVoiceTargets,
  testVoiceCommand,
  showGrammar,
  navigateTableRow,
  selectPatientByIndex,
  clickTableRow,
  getDataGridRows,
  getSelectedDataGridRow,
  ACTION_VERBS_DE,
  ACTION_VERBS_EN,
  ACTION_VERBS_FR,
  SPECIAL_COMMANDS_DE,
  SPECIAL_COMMANDS_EN,
  SPECIAL_COMMANDS_FR,
  PERMANENT_TARGETS_DE,
  PERMANENT_TARGETS_EN,
  PERMANENT_TARGETS_FR
};

export default initFluencyDirect;

